import {noop} from 'lodash';
import React, {FC, useState} from 'react';
import styled, {css} from 'styled-components';

import {buildHoverParentClassName} from '../../../../libs/shared/helpers/src/browser/hoverHelpers';
import {ClickEventHandler, RendererOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {CheckBox} from '../../../../libs/shared/ui/src/components/booleanInputs/checkBox';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {RightOverflowGradient} from './helpers/rightOverflowGradient';
import {getCellBackgroundColor, getCellPointerEventsStyle} from './helpers/tableCellHelpers';
import {virtualTableRowBorderRadius, virtualTableRowCheckboxWrapperWidth} from './helpers/tableHelpers';
import {getStickyStyles} from './tableColumn';

/*
 * Types.
 */

export interface TableRowProps {
  className?: string;
  hasHover?: boolean;

  isSelected?: boolean;
  isDisabled?: boolean;
  shouldShowRightGradient?: boolean;
  shouldAllowMultiSelect?: boolean;

  /** If set, the checkbox will render no matter its state. */
  isCheckboxesVisible?: boolean;

  renderColumn: RendererOf<boolean>;
  onSelect?: ClickEventHandler;
  /** Separate handler for checkbox events if you need different select/checkbox handlers. */
  onCheckBoxClick?: ClickEventHandler;
}

export interface TableRowRenderOptions {
  shouldShowStickyGradient?: boolean;
  shouldShowRightGradient?: boolean;
  shouldAllowMultiSelect?: boolean;
  size?: VisualSizesEnum;
  tableRowHeight: number;
}

/*
 * Styles.
 */

interface StyledTableRowProps {
  $isHovering: boolean;
  $isSelected: boolean;
  $isDisabled?: boolean;
}
const StyledTableRow = styled.div<StyledTableRowProps>`
  position: relative;
  display: flex;
  border-radius: ${virtualTableRowBorderRadius}px;
  flex: 1;

  ${(p) => getCellBackgroundColor(p.$isHovering, p.$isSelected)}
  ${(p) => getCellPointerEventsStyle(p.$isDisabled)}
`;

interface StyledTableRowContainerDivProps {
  $isDisabled?: boolean;
}

const StyledTableRowContainerDiv = styled.div<StyledTableRowContainerDivProps>`
  display: flex;
  flex-flow: row;
`;

const StyledCheckboxWrapper = styled.div`
  display: flex;
  flex: 0 0 ${virtualTableRowCheckboxWrapperWidth}px;
  background: ${(p) => p.theme.greys.white};

  ${getStickyStyles({isSticky: true})}
`;

interface StyledCheckBoxProps {
  $isHovering: boolean;
  $isCheckboxesVisible: boolean;
}

const StyledCheckBox = styled(CheckBox)<StyledCheckBoxProps>`
  margin: auto;
  ${(p) => addOpacityStyles(p.$isHovering, p.$isCheckboxesVisible)}
`;

/*
 * Style Helpers.
 */

function addOpacityStyles(isHovering: boolean, isCheckboxesVisible: boolean) {
  if (!isCheckboxesVisible) {
    return css`
      opacity: ${isHovering ? 1 : 0};
    `;
  }

  return css`
    opacity: 1;
  `;
}

/*
 * Component.
 */

export const TableRow: FC<TableRowProps> = (props) => {
  const {
    className,
    hasHover,
    isSelected,
    shouldShowRightGradient,
    shouldAllowMultiSelect,
    isCheckboxesVisible,
    isDisabled,
    renderColumn,
    onSelect,
    onCheckBoxClick,
  } = props;

  const [isHovering, setIsHovering] = useState(false);
  const updateHoverState = (state: boolean) => {
    if (!hasHover || isDisabled) {
      return;
    }
    setIsHovering(state);
  };

  return (
    <StyledTableRowContainerDiv $isDisabled={isDisabled}>
      <StyledTableRow
        className={buildHoverParentClassName(true, className)}
        onMouseEnter={() => updateHoverState(Boolean(hasHover))}
        onMouseOver={() => updateHoverState(Boolean(hasHover))}
        onMouseLeave={() => updateHoverState(false)}
        $isHovering={isHovering}
        $isDisabled={isDisabled}
        $isSelected={Boolean(isSelected)}
        onClick={(event) => {
          if (onSelect && !event.defaultPrevented) {
            onSelect(event);
          }
        }}
      >
        {maybeRenderRowCheckBox(
          shouldAllowMultiSelect,
          isSelected,
          isCheckboxesVisible,
          isHovering,
          isDisabled,
          onCheckBoxClick,
        )}
        {renderColumn(isHovering)}
      </StyledTableRow>
      {shouldShowRightGradient && <RightOverflowGradient />}
    </StyledTableRowContainerDiv>
  );
};

/*
 * Helpers.
 */

function maybeRenderRowCheckBox(
  shouldAllowMultiSelect?: boolean,
  isSelected?: boolean,
  isCheckboxesVisible?: boolean,
  isHovering?: boolean,
  isDisabled?: boolean,
  onCheckBoxClick?: ClickEventHandler,
) {
  if (!shouldAllowMultiSelect) {
    return null;
  }
  return (
    <StyledCheckboxWrapper onClick={onCheckBoxClick}>
      {!isDisabled && (
        <StyledCheckBox
          $isHovering={Boolean(isHovering)}
          $isCheckboxesVisible={Boolean(isCheckboxesVisible)}
          isChecked={isSelected}
          onChange={noop}
        />
      )}
    </StyledCheckboxWrapper>
  );
}
